import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { NAV, PLAYER } from '../../mock/data';
import { useDemo } from '../../context/DemoContext';
import FastPay from '../xc/FastPay';
import EarlyAccess from '../xc/EarlyAccess';
import { InvestorToggle } from '../xc/InvestorNote';
import { Bell, Settings, Coins, Wallet, Menu, X, ShieldCheck, ScrollText, RotateCcw, ChevronRight, ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const PLAYER_MODES = [
  { id: 'available', label: 'Available for Missions', short: 'AVAILABLE', color: '#2FBF71' },
  { id: 'casino', label: 'Casino Only', short: 'CASINO ONLY', color: '#B026FF' },
  { id: 'social', label: 'Social / Exploring', short: 'SOCIAL / EXPLORING', color: '#E0B85A' },
  { id: 'dnd', label: 'Do Not Disturb', short: 'DO NOT DISTURB', color: '#FF204E' },
];

function Logo() {
  return (
    <NavLink to="/" className="flex items-center gap-2 group">
      <div className="relative h-8 w-8 rounded-md flex items-center justify-center"
           style={{ background: 'linear-gradient(135deg,#D90035,#7A18FF)' }}>
        <span className="font-display font-700 text-white text-lg leading-none">X</span>
        <div className="absolute inset-0 rounded-md xc-pulse" style={{ boxShadow: '0 0 22px 2px rgba(176,38,255,0.6)' }} />
      </div>
      <span className="font-display font-700 tracking-[0.2em] text-white text-lg">XINCITY<sup className="text-[0.5em] tracking-normal align-super text-white/70">™</sup></span>
    </NavLink>
  );
}

export function TopNav() {
  const { loyaltyCoins, demoBalance, notifications, markNotificationsRead, resetDemo, playerMode, setPlayerMode } = useDemo();
  const [mobileOpen, setMobileOpen] = useState(false);
  const unread = notifications.filter((n) => n.unread).length;
  const mode = PLAYER_MODES.find((m) => m.id === playerMode) || PLAYER_MODES[0];

  return (
    <header className="sticky top-0 z-50 xc-glass border-b border-white/5">
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink key={item.path} to={item.path}
                className={({ isActive }) => `px-3.5 py-2 rounded-md text-sm font-500 tracking-wide transition-colors ${isActive ? 'text-white bg-white/5 border border-[#FF204E]/30' : 'text-white/60 hover:text-white'}`}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2 lg:gap-3">
          <EarlyAccess />
          {/* Player mode indicator */}
          <DropdownMenu>
            <DropdownMenuTrigger className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md border bg-white/[0.03] hover:bg-white/[0.06] transition-colors outline-none" style={{ borderColor: `${mode.color}55` }}>
              <span className="h-2 w-2 rounded-full" style={{ background: mode.color, boxShadow: `0 0 8px ${mode.color}` }} />
              <span className="text-[11px] font-600 tracking-[0.1em] text-white/85">{mode.short}</span>
              <ChevronDown className="h-3.5 w-3.5 text-white/40" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 bg-[#151119] border-white/10 text-white">
              <DropdownMenuLabel className="text-white/60">Player Mode</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              {PLAYER_MODES.map((m) => (
                <DropdownMenuItem key={m.id} onClick={() => setPlayerMode(m.id)} className="cursor-pointer focus:bg-white/5 flex items-center gap-2.5">
                  <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: m.color, boxShadow: `0 0 8px ${m.color}` }} />
                  <span className="flex-1 text-sm">{m.label}</span>
                  {m.id === playerMode && <Check className="h-4 w-4" style={{ color: m.color }} />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-white/10" />
              <div className="px-2 py-1.5 text-[11px] text-white/40 leading-relaxed">Missions are always optional. Your mode controls how visible you are to crews.</div>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#E0B85A]/25 bg-[#E0B85A]/5">
            <Coins className="h-4 w-4 text-[#E0B85A]" />
            <span className="text-sm font-600 text-[#E0B85A]">{loyaltyCoins.toLocaleString()} LC</span>
          </div>
          <div className="hidden md:flex"><FastPay /></div>

          {/* Notifications */}
          <DropdownMenu onOpenChange={(o) => o && markNotificationsRead()}>
            <DropdownMenuTrigger className="relative h-9 w-9 grid place-items-center rounded-md border border-white/10 bg-white/5 hover:border-[#B026FF]/40 transition-colors outline-none">
              <Bell className="h-4 w-4 text-white/80" />
              {unread > 0 && <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full text-[10px] grid place-items-center text-white" style={{ background: '#FF204E' }}>{unread}</span>}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 bg-[#151119] border-white/10 text-white">
              <DropdownMenuLabel className="text-white/60">Notifications</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              {notifications.map((n) => (
                <div key={n.id} className="px-2 py-2 text-sm text-white/80 flex gap-2 items-start">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: n.unread ? '#FF204E' : '#4a4a55' }} />
                  {n.text}
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Avatar */}
          <NavLink to="/profile" className="hidden sm:flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-white/10 hover:border-[#FF204E]/40 transition-colors">
            <span className="h-7 w-7 rounded-full grid place-items-center text-white text-sm font-600" style={{ background: 'linear-gradient(135deg,#D90035,#7A18FF)' }}>{PLAYER.avatarInitial}</span>
            <span className="text-sm text-white/90 leading-tight">
              {PLAYER.name}
              <span className="block text-[10px] text-[#E0B85A]">LVL {PLAYER.level}</span>
            </span>
          </NavLink>

          {/* Settings */}
          <DropdownMenu>
            <DropdownMenuTrigger className="h-9 w-9 grid place-items-center rounded-md border border-white/10 bg-white/5 hover:border-[#B026FF]/40 transition-colors outline-none">
              <Settings className="h-4 w-4 text-white/80" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#151119] border-white/10 text-white">
              <DropdownMenuLabel className="text-white/60">{PLAYER.name} — {PLAYER.crew}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <NavLink to="/safe-play"><DropdownMenuItem className="cursor-pointer focus:bg-white/5"><ShieldCheck className="h-4 w-4 mr-2 text-[#2FBF71]" />Safe Play</DropdownMenuItem></NavLink>
              <NavLink to="/trust"><DropdownMenuItem className="cursor-pointer focus:bg-white/5"><ScrollText className="h-4 w-4 mr-2 text-[#B026FF]" />Trust &amp; Safety</DropdownMenuItem></NavLink>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={resetDemo} className="cursor-pointer focus:bg-white/5 text-[#FF204E]"><RotateCcw className="h-4 w-4 mr-2" />Reset Demo</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button className="lg:hidden h-9 w-9 grid place-items-center rounded-md border border-white/10 bg-white/5" onClick={() => setMobileOpen((v) => !v)}>
            {mobileOpen ? <X className="h-4 w-4 text-white" /> : <Menu className="h-4 w-4 text-white" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-white/10 bg-[#0D0B10] px-4 py-3">
          {NAV.map((item) => (
            <NavLink key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `flex items-center justify-between px-3 py-3 rounded-md text-sm ${isActive ? 'text-white bg-white/5' : 'text-white/70'}`}>
              {item.label} <ChevronRight className="h-4 w-4 opacity-50" />
            </NavLink>
          ))}

          <div className="my-3 border-t border-white/10" />

          {/* Early Access */}
          <div className="mb-3"><EarlyAccess variant="mobile" /></div>

          {/* Balances + Fast Pay */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-[#E0B85A]/25 bg-[#E0B85A]/5 mb-2">
            <Coins className="h-4 w-4 text-[#E0B85A]" />
            <span className="text-sm font-600 text-[#E0B85A]">{loyaltyCoins.toLocaleString()} LC</span>
          </div>
          <div className="mb-3"><FastPay mobile /></div>

          {/* Player mode selector */}
          <div className="px-1 text-[11px] uppercase tracking-[0.16em] text-white/40 mb-2">Player Mode</div>
          <div className="grid grid-cols-2 gap-2">
            {PLAYER_MODES.map((m) => (
              <button key={m.id} onClick={() => setPlayerMode(m.id)}
                className="flex items-center gap-2 px-3 py-2.5 rounded-md border text-left"
                style={{ borderColor: m.id === playerMode ? m.color : 'rgba(255,255,255,0.08)', background: m.id === playerMode ? `${m.color}12` : 'transparent' }}>
                <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: m.color, boxShadow: `0 0 8px ${m.color}` }} />
                <span className="text-xs text-white/85 flex-1">{m.label}</span>
                {m.id === playerMode && <Check className="h-3.5 w-3.5" style={{ color: m.color }} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const links = [
    { label: '18+', to: '/safe-play' },
    { label: 'Responsible Gaming', to: '/safe-play' },
    { label: 'Privacy', to: '/trust' },
    { label: 'Terms', to: '/trust' },
    { label: 'Trust & Safety', to: '/trust' },
  ];
  return (
    <footer className="border-t border-white/5 bg-[#070609] mt-auto">
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-display font-700 tracking-[0.2em] text-white">XINCITY<sup className="text-[0.5em] tracking-normal align-super text-white/70">™</sup></span>
              <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#FF204E]/30 text-[#FF204E]">PROTOTYPE</span>
            </div>
            <p className="text-xs text-white/40 max-w-md">Live the city. Play your way. An investor prototype — no real money, deposits or gambling. All values are demo data.</p>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {links.map((l, i) => (
              <NavLink key={i} to={l.to} className="text-sm text-white/50 hover:text-[#FF204E] transition-colors">{l.label}</NavLink>
            ))}
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="h-6 w-6 grid place-items-center rounded-full border border-[#E0B85A]/40 text-[10px] text-[#E0B85A] font-600">18</span>
            <p className="text-xs text-white/40">Play responsibly. Responsible-gambling controls always override XINCITY™ mechanics.</p>
          </div>
          <p className="text-xs text-white/40">This is a <span className="text-white/70 font-500">Xini Engine</span> product. XINCITY™ is a trademark of Xini Engine.</p>
        </div>
      </div>
    </footer>
  );
}

export default function AppShell({ children }) {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="min-h-screen flex flex-col bg-[#070609]">
      <TopNav />
      <main className="flex-1">{children}</main>
      <Footer />
      <InvestorToggle />
    </div>
  );
}
